import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Chip,
  Alert,
  Card,
  CardActionArea,
  CardContent,
} from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import ControlledDataGrid from '../../components/DataGrid/ControlledDataGrid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import moment from 'moment';
import axios from 'axios';
import { APIURL } from '../../config';
import ReferralDetailsModal from './ReferralDetailsModal';

const STAGE = {
  DRAFT: 'DRAFT',
  SUBMITTED: 'SUBMITTED',
  UNDER_REVIEW: 'UNDER_REVIEW',
  ACCEPTED: 'ACCEPTED',
  REJECTED: 'REJECTED',
};

const STATUS_TO_STAGE = {
  DRAFT: STAGE.DRAFT,
  PENDING: STAGE.SUBMITTED,
  ACTIVE: STAGE.SUBMITTED,
  REFERRAL_INITIATED: STAGE.SUBMITTED,
  INITIAL_CONTACT: STAGE.SUBMITTED,
  DOCUMENTATION_PENDING: STAGE.UNDER_REVIEW,
  DOCUMENTATION_RECEIVED: STAGE.UNDER_REVIEW,
  DETERMINATION_PENDING: STAGE.UNDER_REVIEW,
  INTAKE_IN_PROGRESS: STAGE.UNDER_REVIEW,
  ON_HOLD: STAGE.UNDER_REVIEW,
  ACCEPTED: STAGE.ACCEPTED,
  CLOSED: STAGE.ACCEPTED,
  DENIED: STAGE.REJECTED,
  REJECTED: STAGE.REJECTED,
};

const STAGE_META = {
  [STAGE.DRAFT]:        { label: 'Drafts',       color: '#6c757d' },
  [STAGE.SUBMITTED]:    { label: 'Submitted',    color: '#3fa7d6' },
  [STAGE.UNDER_REVIEW]: { label: 'Under Review', color: '#7b61ff' },
  [STAGE.ACCEPTED]:     { label: 'Accepted',     color: '#28a745' },
  [STAGE.REJECTED]:     { label: 'Rejected',     color: '#dc3545' },
};

const SLA_STALLED_DAYS = 5;
const SLA_URGENT_DAYS = 10;

const getCustomerDisplayName = (row) => {
  const candidate = row?.customer_name;
  return candidate && String(candidate).trim() ? String(candidate).trim() : '—';
};

const resolvePartnerName = (row, mode, currentCompanyId) => {
  const isInbound = mode === 'inbound';
  // prefer the pre-resolved COALESCE field from the backend, fall back to individual fields
  const resolved = isInbound
    ? (row?.initiator_name || row?.initiator_com_name || row?.initiator_external_com_name || row?.initiator_external_com_id || row?.initiator_com_id)
    : (row?.receiver_name || row?.receiver_com_name || row?.receiver_external_com_name || row?.receiver_external_com_id || row?.receiver_com_id);

  if (resolved && String(resolved).trim() && String(resolved).trim() !== String(currentCompanyId)) {
    return String(resolved).trim();
  }
  return '—';
};

const computeAlert = (row) => {
  if (row.stage === STAGE.ACCEPTED || row.stage === STAGE.REJECTED) return null;
  if (!row.createdAt) return null;
  const days = moment().diff(moment(row.createdAt), 'days');
  if (days >= SLA_URGENT_DAYS) return 'URGENT';
  if (days >= SLA_STALLED_DAYS) return 'STALLED';
  return null;
};

const StatusCard = ({ label, count, active, onClick, color }) => (
  <Card
    variant="outlined"
    sx={{
      flex: 1,
      borderColor: active ? color : '#e0e6ef',
      borderLeft: `4px solid ${color}`,
      bgcolor: active ? `${color}10` : '#fff',
      transition: 'border-color 120ms, background-color 120ms',
    }}
  >
    <CardActionArea onClick={onClick} sx={{ px: 2, py: 1.25 }}>
      <CardContent sx={{ p: '0 !important' }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#1a1a2e', lineHeight: 1.1, mt: 0.25 }}>
          {count}
        </Typography>
      </CardContent>
    </CardActionArea>
  </Card>
);

const STATUS_LABEL = {
  PENDING: 'Pending',
  ACTIVE: 'Active',
  REFERRAL_INITIATED: 'Referral Initiated',
  INITIAL_CONTACT: 'Initial Contact',
  DOCUMENTATION_PENDING: 'Documentation Pending',
  DOCUMENTATION_RECEIVED: 'Documentation Received',
  DETERMINATION_PENDING: 'Determination Pending',
  INTAKE_IN_PROGRESS: 'Intake in Progress',
  ON_HOLD: 'On Hold',
  ACCEPTED: 'Accepted',
  CLOSED: 'Closed',
  DENIED: 'Denied',
  REJECTED: 'Rejected',
  DRAFT: 'Draft',
};

const StageChip = ({ stage, alert, rawStatus }) => {
  const label = STATUS_LABEL[rawStatus] || rawStatus || '';
  if (stage === STAGE.ACCEPTED) {
    return <Chip size="small" color="success" variant="outlined" icon={<CheckCircleIcon />} label={label} sx={{ fontWeight: 700 }} />;
  }
  if (stage === STAGE.REJECTED) {
    return <Chip size="small" color="error" variant="outlined" icon={<CancelIcon />} label={label} sx={{ fontWeight: 700 }} />;
  }
  if (stage === STAGE.DRAFT) {
    return <Chip size="small" variant="outlined" label={label} sx={{ fontWeight: 700, color: '#555', borderColor: '#bbb' }} />;
  }
  if (stage === STAGE.SUBMITTED) {
    return <Chip size="small" color="info" variant="outlined" label={label} sx={{ fontWeight: 700 }} />;
  }
  if (stage === STAGE.UNDER_REVIEW) {
    const isUrgent = alert === 'URGENT';
    const isStalled = alert === 'STALLED';
    return (
      <Chip
        size="small"
        variant={isUrgent ? 'filled' : 'outlined'}
        icon={isUrgent ? <ErrorOutlineIcon /> : isStalled ? <WarningAmberIcon /> : undefined}
        label={label}
        sx={{
          fontWeight: 700,
          color: isUrgent ? '#fff' : '#7b61ff',
          borderColor: isUrgent ? '#dc3545' : isStalled ? '#f0ad4e' : '#7b61ff',
          bgcolor: isUrgent ? '#dc3545' : isStalled ? '#fff9eb' : undefined,
          '& .MuiChip-icon': { color: isUrgent ? '#fff' : '#f0ad4e' },
        }}
      />
    );
  }
  return null;
};

const OutboundReferralsControlRoom = ({ comp_id, user, mode = 'outbound' }) => {
  const apiRef = useGridApiRef();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [filterStage, setFilterStage] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${APIURL}/referral`, { params: { comp_id } });
      const arr = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);
      const expectedRole = mode === 'inbound' ? 'receiving' : 'initiated';
      const filtered = arr.filter(r => {
        const role = r.company_role || (r.initiator_com_id === comp_id ? 'initiated' : (r.receiver_com_id === comp_id ? 'receiving' : ''));
        return role === expectedRole;
      });
      const mapped = filtered.map((r, index) => {
        const rawStatus = String(r.referral_status || r.status || 'PENDING').toUpperCase().replace(/ /g, '_');
        const stage = STATUS_TO_STAGE[rawStatus] || STAGE.SUBMITTED;
        const row = {
          id: r.referral_id || index,
          referral_id: r.referral_id,
          clientName: getCustomerDisplayName(r),
          partnerName: resolvePartnerName(r, mode, comp_id),
          subject: r.referral_subject || 'Referral',
          stage,
          rawStatus,
          createdAt: r.referral_date || r.created_at,
          raw: r,
        };
        row.alert = computeAlert(row);
        return row;
      });
      setRows(mapped);
    } catch (e) {
      console.error(`${mode === 'inbound' ? 'Inbound' : 'Outbound'} referral fetch error`, e);
      setError(`Unable to load ${mode === 'inbound' ? 'inbound' : 'outbound'} referrals.`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [comp_id, mode]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const counts = useMemo(() => {
    const c = { DRAFT: 0, SUBMITTED: 0, UNDER_REVIEW: 0, ACCEPTED: 0, REJECTED: 0 };
    rows.forEach(r => { c[r.stage] = (c[r.stage] || 0) + 1; });
    return c;
  }, [rows]);

  const visibleRows = useMemo(() => {
    if (!filterStage) return rows;
    return rows.filter(r => r.stage === filterStage);
  }, [rows, filterStage]);

  const handleOpenDetails = (row) => {
    setSelectedReferral(row.raw);
    setDetailsOpen(true);
  };

  const columns = useMemo(() => [
    { field: 'clientName', headerName: 'Client', flex: 1, minWidth: 140, renderCell: (p) => <strong>{p.value}</strong> },
    { field: 'partnerName', headerName: mode === 'inbound' ? 'Referred By' : 'Referred To', flex: 1, minWidth: 160 },
    { field: 'subject', headerName: 'Subject', flex: 1.5, minWidth: 160 },
    {
      field: 'rawStatus',
      headerName: 'Status',
      width: 190,
      renderCell: (p) => <StageChip stage={p.row.stage} alert={p.row.alert} rawStatus={p.row.rawStatus} />,
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 130,
      type: 'date',
      // slice to date part before constructing to avoid UTC offset shifting the day
      valueGetter: (value) => value ? new Date(String(value).slice(0, 10) + 'T00:00:00') : null,
      valueFormatter: (value) => value ? moment(value).format('MMM D, YYYY') : '—',
    },
  ], []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', p: 2, height: 'calc(100vh - 130px)', overflow: 'hidden' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 1.5 }}>
          {mode === 'inbound' ? 'Received Referrals' : 'Sent Referrals'}
        </Typography>

        <Stack direction="row" spacing={1.25} sx={{ mb: 1.5 }}>
          {Object.entries(STAGE_META).map(([stage, meta]) => (
            <StatusCard
              key={stage}
              label={meta.label}
              count={counts[stage] || 0}
              color={meta.color}
              active={filterStage === stage}
              onClick={() => setFilterStage(filterStage === stage ? null : stage)}
            />
          ))}
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <ControlledDataGrid
            gridId={76}
            user={user}
            apiRef={apiRef}
            rows={visibleRows}
            columns={columns}
            loading={loading}
            rowHeight={36}
            getRowId={(row) => row.id}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 1 ? 'report-row-alternate' : ''
            }
            disableRowSelectionOnClick
            onRowClick={(params) => handleOpenDetails(params.row)}
            initialState={{
              sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] },
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            pagination
            pageSizeOptions={[10, 25, 50, 100]}
            sx={{
              height: '100%',
              '& .report-row-alternate .MuiDataGrid-cell, & .report-row-alternate .MuiDataGrid-filler--pinnedLeft': {
                backgroundColor: '#EAF4FC',
              },
              '& .report-row-alternate:hover .MuiDataGrid-cell, & .report-row-alternate:hover .MuiDataGrid-filler--pinnedLeft': {
                backgroundColor: '#DCEFFC',
              },
            }}
          />
        </Box>

      <ReferralDetailsModal
        open={detailsOpen}
        onClose={() => { setDetailsOpen(false); fetchRows(); }}
        referral={selectedReferral}
        comp_id={comp_id}
        user={user}
      />
    </Box>
  );
};

export default OutboundReferralsControlRoom;
